import React, { useEffect, useRef, useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { useVoiceSession } from "../hooks/useVoiceSession";
import BotMessageBox from "../../components/messages/BotMessageBox";
import {
  botMarkdownComponents,
  widgetUrlTransform,
} from "../../components/messages/mapping/CustomLinkRenderer";
import MessageMetadata from "../../components/messages/MessageMetadata";
import getInternalStyle from "../../../../utils/Styles";
import "../voice.css";

interface Props {
  assistantId: string;
  threadId: string | null;
  isMobile?: boolean;
  language: string;
  style: { color: string };
  showHandoffButton: boolean;
  assistantChatConfiguration?: any;
  setFullScreen: (updater: (prev: any) => any) => void;
  onTurnComplete?: (transcript: string, response: string, metadata: any[] | null) => void;
  onThreadIdChange?: (threadId: string) => void;
  onClose: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  idle: "Pronta",
  connecting: "Schiarendomi la voce..",
  listening: "Ascoltando",
  error: "Errore di connessione",
  warming: "Sto scaldando la voce...",
};

const WAVEFORM_BARS = 9;

export const VoicePanel: React.FC<Props> = ({
  assistantId,
  threadId,
  isMobile = false,
  language,
  style,
  showHandoffButton,
  assistantChatConfiguration,
  setFullScreen,
  onTurnComplete,
  onThreadIdChange,
  onClose,
}) => {
  const { state, start, stop, getAnalyser } = useVoiceSession(assistantId, threadId, isMobile, onThreadIdChange, onClose);
  const { status, currentTurn, waterfallData } = state;
  const styles = getInternalStyle(assistantId, style);
  const [warming, setWarming] = React.useState(true);

  // Same link treatment as the text chat — see CustomLinkRenderer.
  const botComponents = useMemo(
    () => botMarkdownComponents({ assistantId, language, style }),
    [assistantId, language, style]
  );

  // Auto-scroll: refs for the scrollable body and the bottom sentinel
  const bodyRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Delay start by 10 s to let the backend warm up (cold start), then auto-connect
  useEffect(() => {
    const warmUp = setTimeout(() => {
      setWarming(false);
      start();
    }, 5000);
    return () => {
      clearTimeout(warmUp);
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notify parent when a voice turn completes so it can persist to chat history
  const prevWaterfallRef = useRef(waterfallData);
  useEffect(() => {
    if (
      waterfallData &&
      waterfallData !== prevWaterfallRef.current &&
      waterfallData.transcript &&
      waterfallData.response
    ) {
      prevWaterfallRef.current = waterfallData;
      onTurnComplete?.(waterfallData.transcript, waterfallData.response, waterfallData.metadata ?? null);
    }
  }, [waterfallData, onTurnComplete]);

  // Waveform: bar element refs for direct DOM updates (no React re-render per frame)
  const barRefs = useRef<(HTMLDivElement | null)[]>(Array(WAVEFORM_BARS).fill(null));
  const animFrameRef = useRef<number | null>(null);

  const displayStatus = warming ? "warming" : status;
  const isListening = status === "listening" && !warming;

  useEffect(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }

    if (!isListening) {
      // Reset to idle scaleY — matches CSS .voice-waveform--idle rule
      barRefs.current.forEach((bar) => {
        if (bar) bar.style.transform = "scaleY(0.14)";
      });
      return;
    }

    const freqData = new Uint8Array(128);
    
    // just for drawing the waveforms bars as the input voice
    const draw = () => {
      const analyser = getAnalyser();
      if (analyser) {
        analyser.getByteFrequencyData(freqData);
        const bars = barRefs.current;
        const rangeEnd = Math.floor(freqData.length * 0.45);
        const sliceWidth = Math.floor(rangeEnd / WAVEFORM_BARS);
        bars.forEach((bar, i) => {
          if (!bar) return;
          let sum = 0;
          const start = i * sliceWidth;
          for (let j = 0; j < sliceWidth; j++) sum += freqData[start + j];
          const avg = sum / sliceWidth;          
          const scale = 0.14 + (avg / 255) * 0.86;
          bar.style.transform = `scaleY(${scale.toFixed(3)})`;
        });
      }
      animFrameRef.current = requestAnimationFrame(draw);
    };

    animFrameRef.current = requestAnimationFrame(draw);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isListening, getAnalyser]);

  // Scroll to bottom whenever transcript or response updates
  useEffect(() => {
    scrollToBottom();
  }, [currentTurn.transcript, currentTurn.response, scrollToBottom]);

  const transcript =
    currentTurn.transcript || (waterfallData?.transcript ?? "");

  const hasResponse = !!currentTurn.response;

  return (
    <div className="voice-panel">
      {/* Sticky header — status + waveform never scroll away */}
      <div className="voice-panel__header">
        {/* Status bar */}
        <div className="voice-status-bar">
          <div className={`voice-status-dot voice-status-dot--${displayStatus}`} />
          <span className="voice-status-text">{STATUS_LABELS[displayStatus] ?? displayStatus}</span>
        </div>

        {/* Waveform — JS-driven when listening, CSS shimmer when responding, flat otherwise */}
        <div
          className={`voice-waveform${
            !isListening ? " voice-waveform--idle" : " voice-waveform--live"
          }${hasResponse ? " voice-waveform--responding" : ""}`}
        >
          {Array.from({ length: WAVEFORM_BARS }).map((_, i) => (
            <div
              key={i}
              ref={(el) => { barRefs.current[i] = el; }}
              className="voice-waveform__bar"
            />
          ))}
        </div>
      </div>

      {/* Scrollable messages area */}
      <div className="voice-panel__body" ref={bodyRef}>
        {/* Live transcript — user bubble, or placeholder when silent */}
        {transcript ? (
          <div
            className="widget-user-message"
            style={{ marginBottom: "10px" }}
          >
            {transcript}
          </div>
        ) : (
          <div className="voice-transcript--empty">
            Say something&hellip;
          </div>
        )}

        {/* Agent response — same bot bubble as regular chat */}
        {currentTurn.response && (
          <BotMessageBox
            assistantId={assistantId}
            style={style}
            getInternalStyle={getInternalStyle}
          >
            <div
              className="widget-chatbot-message"
              style={{
                marginBottom: "10px",
                backgroundColor: styles.messages.bot.color,
              }}
            >
              <ReactMarkdown
                urlTransform={widgetUrlTransform}
                components={botComponents}
              >
                {currentTurn.response}
              </ReactMarkdown>
            </div>
          </BotMessageBox>
        )}

        {currentTurn.metadata && currentTurn.metadata.length > 0 && (
          <MessageMetadata
            metadata={currentTurn.metadata}
            style={style}
            isMobile={isMobile}
            setFullScreen={setFullScreen}
            showHandoffButton={showHandoffButton}
            language={language}
            assistantChatConfiguration={assistantChatConfiguration}
          />
        )}

        {/* Scroll sentinel — scrollIntoView targets this */}
        <div ref={bottomRef} />
      </div>

      {/* Floating end-call button — overlaid at bottom-center, doesn't push content */}
      <div className="voice-panel__footer">
        <button
          className="voice-end-call-btn"
          onClick={() => { stop(); onClose(); }}
          aria-label="End call"
          title="End call"
        >
          <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
            <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08C.11 12.9 0 12.65 0 12.37c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.1-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
          </svg>
        </button>
      </div>
    </div>
  );
};
